import { useState, useEffect } from "react";
import supabase from "../supabase";
import { useRenovCredits } from "../context/RenovCreditsContext";
import { useNotification } from "../context/NotificationContext";
import axios from 'axios';

export default function useRoomAnalysis(
  room,
  superficieInit,
  initialComment,
  initialResult,
  userPlan,
  localisationProjet,
  superficieTotale,
  ageLogementAnnees
) {
  const [analysisResult, setAnalysisResult] = useState(initialResult || null);
  const [analyzing, setAnalyzing] = useState(false);
  const [comment, setComment] = useState(initialComment || "");
  const [analysisType, setAnalysisType] = useState("standard");
  const [isGeneratingVisuals, setIsGeneratingVisuals] = useState(false);

  const { renovCredits, setRenovCredits } = useRenovCredits();
  const { showNotification } = useNotification();

  useEffect(() => {
    if (room) {
      setComment(room.commentaire_user || "");
      setAnalysisResult(room.analyse_json || null);
      setAnalysisType(room.analysis_type || "standard");
    }
  }, [room]);

  const extractMimeTypeAndBase64 = (dataUrl) => {
    const matches = dataUrl.match(/^data:(.*?);base64,(.*)$/);
    if (!matches || matches.length !== 3) {
      throw new Error("Image invalide (base64 manquant).");
    }
    return { mimeType: matches[1], base64Data: matches[2] };
  };

  const base64ToBlob = (base64, mimeType = "image/jpeg") => {
    const byteCharacters = atob(base64);
    const byteNumbers = Array.from(byteCharacters).map((char) =>
      char.charCodeAt(0)
    );
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  };

  const handleAnalyzeRoom = async (images, superficie, analysisTypeToUse, options = {}) => {
    if (!room || images.length === 0) return { success: false, error: "Données de pièce ou images manquantes." };

    const cost = userPlan === "Free" ? 10 : 5;
    if (renovCredits < cost) {
      showNotification(
        `Vous n'avez pas assez de RenovCredits pour lancer cette analyse. Il vous faut ${cost} RC.`,
        "warning"
      );
      return { success: false, error: "Crédits insuffisants." };
    }

    setAnalyzing(true);
    setAnalysisResult(null);
    setAnalysisType(analysisTypeToUse);

    let userIdToUse;
    let visualResults = [];

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error("Impossible de récupérer l'utilisateur.");
      userIdToUse = user.id;

      // --- Étape 1: Analyse textuelle ---
      const formData = new FormData();
      images.forEach((image, i) => {
        const { mimeType, base64Data } = extractMimeTypeAndBase64(image.url);
        const blob = base64ToBlob(base64Data, mimeType);
        formData.append("images", blob, `image-${i}.${mimeType.split("/")[1]}`);
      });
      formData.append("surface", superficie);
      formData.append("comment", comment);
      // ... autres champs ...

      const endpoint = analysisTypeToUse === "corps" ? "/openai/analyze-image-by-corps" : "/openai/analyze-image";
      const response = await fetch(process.env.REACT_APP_SERVER_URL + endpoint, { method: "POST", body: formData });

      if (!response.ok) {
        throw new Error(`Erreur API: ${response.status} ${response.statusText}`);
      }

      const rawResponse = await response.json();
      const parsedResult = typeof rawResponse.responses[0] === 'string' ? JSON.parse(rawResponse.responses[0]) : rawResponse.responses[0];
      const finalResult = ensureAnalysisResultStructure(parsedResult);
      setAnalysisResult(finalResult);

      const { data: analysisData, error: insertError } = await supabase
        .from("room_analyses")
        .insert({
          room_id: room.id,
          analysis_result: finalResult,
          user_comment: comment,
          analysis_type: analysisTypeToUse,
          user_id: userIdToUse,
        })
        .select();

      if (insertError) throw insertError;
      showNotification("Analyse sauvegardée avec succès.", "success");

      await supabase.from("profiles").update({ renovcredits: renovCredits - cost }).eq("id", userIdToUse);
      setRenovCredits((prev) => prev - cost);

      // --- Étape 2: Génération d'images (si activée) ---
      if (options.shouldGenerateVisuals) {
        setIsGeneratingVisuals(true);
        const analysisRecord = analysisData?.[0];
        const analysisId = analysisRecord?.id;

        const imagePromises = images.map((image) => {
          const formData = new FormData();
          formData.append('prompt', comment);
          formData.append('roomId', room.id);
          formData.append('userId', userIdToUse);
          if (analysisId) {
            formData.append('analysisId', String(analysisId));
          }
          formData.append('imageUrl', image.url);

          return axios
            .post(`${process.env.REACT_APP_SERVER_URL}/google/edit-image`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            })
            .then((res) => {
              const hasImages = Array.isArray(res.data.images) && res.data.images.length > 0;
              if (!hasImages && res.data.error) {
                console.warn('Google edit-image warning:', res.data.error);
                showNotification(res.data.error, 'warning');
              }
              return ({
                original: image.url,
                modified: res.data.images?.[0] || null,
                success: hasImages,
                analysisId,
                modification: res.data.modification || null,
              });
            })
            .catch((err) => {
              console.error("Erreur de generation d'image pour:", image.url, err);
              return { original: image.url, modified: null, success: false, analysisId };
            });
        });

        const results = await Promise.all(imagePromises);
        visualResults = results
          .filter((r) => r.success && r.modified)
          .map((r) => ({
            original: r.original,
            modified: r.modified,
            analysisId: r.analysisId,
            modification: r.modification || null,
          }));
        setIsGeneratingVisuals(false);
      }

      return { success: true, analysis: analysisData[0], visuals: visualResults };

    } catch (err) {
      console.error("Erreur analyse :", err);
      setAnalysisResult({ error: "Erreur inconnue lors de l’analyse." });
      showNotification(err.message, "danger");
      return { success: false, error: err.message, visuals: [] };
    } finally {
      setAnalyzing(false);
      setIsGeneratingVisuals(false);
    }
  };

  const hasEnoughCredits = (type) => {
    const cost = userPlan === "Free" ? 10 : 5;
    return renovCredits >= cost;
  };

  const ensureAnalysisResultStructure = (result) => {
    // ... (la fonction reste la même)
    return {
      note_etat_piece: result?.note_etat_piece ?? 0,
      type_renovation_generale:
        result?.type_renovation_generale ?? "Non spécifié",
      localisation_projet: result?.localisation_projet ?? "Non spécifiée",
      surface_habitable_totale_logement_m2:
        result?.surface_habitable_totale_logement_m2 ?? 0,
      surface_piece_a_renover_m2: result?.surface_piece_a_renover_m2 ?? 0,
      age_logement_annees: result?.age_logement_annees ?? 0,
      taux_tva_applique: result?.taux_tva_applique ?? 0,
      surface_vitree_totale_proposee_m2:
        result?.surface_vitree_totale_proposee_m2 ?? 0,
      conformite_surface_vitree_re2020:
        result?.conformite_surface_vitree_re2020 ?? false,
      cout_total_estime_ht: result?.cout_total_estime_ht ?? 0,
      cout_total_imprevus_ht: result?.cout_total_imprevus_ht ?? 0,
      cout_total_estime_ttc: result?.cout_total_estime_ttc ?? 0,
      commentaires_generaux:
        result?.commentaires_generaux ?? "Aucun commentaire général.",
      corps_de_metier: result?.corps_de_metier ?? [],
    };
  };

  return {
    handleAnalyzeRoom,
    analysisResult,
    analyzing,
    setAnalyzing,
    comment,
    setComment,
    analysisType,
    hasEnoughCredits,
    isGeneratingVisuals,
  };
}
